import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import * as stagesApi from './api'
import type { Stage, StageRequest, StageUpdateRequest, StageOwner } from './types'
import type { StatusBadgeVariant } from '@/components/common/StatusBadge'

export type StageInfo = { label: string; color: StatusBadgeVariant }

/** A bare string is a configuration code (back-compat); otherwise an explicit owner. */
function toOwner(owner: string | StageOwner): StageOwner {
  return typeof owner === 'string' ? { kind: 'configuration', code: owner } : owner
}

export const stageKeys = {
  byOwner: (owner: string | StageOwner) => {
    const o = toOwner(owner)
    return ['stages', o.kind, o.code] as const
  },
}

export function useStages(owner: string | StageOwner) {
  const o = toOwner(owner)
  return useQuery({
    queryKey: stageKeys.byOwner(o),
    queryFn: () => stagesApi.listStages(o),
    enabled: !!o.code,
  })
}

/**
 * Loads stages for many configurations in parallel and returns a nested label map:
 * {@code configurationCode -> stageCode -> labelPl}. Used by list views that mix
 * contracts of different configurations (each row resolves its own label).
 *
 * @deprecated Prefer {@link useStageInfoByConfigurations} which also carries color.
 */
export function useStageLabelsByConfigurations(configurationCodes: string[]) {
  const unique = useMemo(() => Array.from(new Set(configurationCodes.filter(Boolean))), [configurationCodes])
  const queries = useQueries({
    queries: unique.map((pc) => ({
      queryKey: stageKeys.byOwner(pc),
      queryFn: () => stagesApi.listStages(pc),
    })),
  })
  return useMemo(() => {
    const out = new Map<string, Map<string, string>>()
    unique.forEach((pc, i) => {
      const data = queries[i]?.data as Stage[] | undefined
      if (!data) return
      const inner = new Map<string, string>()
      data.forEach((s) => inner.set(s.code, s.labelPl))
      out.set(pc, inner)
    })
    return out
  }, [unique, queries])
}

/**
 * Loads stages for many configurations in parallel and returns a nested info map:
 * {@code configurationCode -> stageCode -> { label, color }}. Use when rendering
 * stage badges - provides both the display label and the semantic color.
 */
export function useStageInfoByConfigurations(configurationCodes: string[]) {
  const unique = useMemo(() => Array.from(new Set(configurationCodes.filter(Boolean))), [configurationCodes])
  const queries = useQueries({
    queries: unique.map((pc) => ({
      queryKey: stageKeys.byOwner(pc),
      queryFn: () => stagesApi.listStages(pc),
    })),
  })
  return useMemo(() => {
    const out = new Map<string, Map<string, StageInfo>>()
    unique.forEach((pc, i) => {
      const data = queries[i]?.data as Stage[] | undefined
      if (!data) return
      const inner = new Map<string, StageInfo>()
      data.forEach((s) => inner.set(s.code, { label: s.labelPl, color: s.color ?? 'neutral' }))
      out.set(pc, inner)
    })
    return out
  }, [unique, queries])
}

function invalidate(qc: ReturnType<typeof useQueryClient>, owner: string | StageOwner) {
  qc.invalidateQueries({ queryKey: stageKeys.byOwner(owner) })
  // gating UI groups by stage, so a stage rename/reorder may shift its layout
  qc.invalidateQueries({ queryKey: ['gating', 'requirements'] })
}

export function useCreateStage(owner: string | StageOwner) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StageRequest) => stagesApi.createStage(owner, payload),
    onSuccess: () => invalidate(qc, owner),
  })
}

export function useUpdateStage(owner: string | StageOwner) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; payload: StageUpdateRequest }) =>
      stagesApi.updateStage(owner, vars.code, vars.payload),
    onSuccess: () => invalidate(qc, owner),
  })
}

export function useDeleteStage(owner: string | StageOwner) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => stagesApi.deleteStage(owner, code),
    onSuccess: () => invalidate(qc, owner),
  })
}

export function useReorderStages(owner: string | StageOwner) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (codes: string[]) => stagesApi.reorderStages(owner, codes),
    onSuccess: () => invalidate(qc, owner),
  })
}
